import { Controller } from "stimulus";

export default class extends Controller {

    static targets = [
        "city", "code", "quantity",
        "singleForm", "textarea",
        "titleIntro", "titleSelected",
        "codesWrap", "highlight", "invalidHint", "submitButton"
    ];

    static values = {
        validateUrl: String,
        loadLabel: String,
        importValidLabel: String,
        emptyLabel: String,
        noValidLabel: String,
        errorLabel: String
    };

    connect() {
        this.reviewMode = false;
        this.invalidCodes = [];
    }

    openModal() {
        this.resetForm();
        $('#loadWarehouseModal').modal('show');

        this.titleIntroTarget.classList.remove('d-none');
        this.titleSelectedTarget.classList.add('d-none');
    }

    cityChanged(event) {
        this.cityTarget.value = event.target.value;
        this.singleFormTarget.classList.remove("d-none");

        this.titleIntroTarget.classList.add('d-none');
        this.titleSelectedTarget.classList.remove('d-none');
        setTimeout(() => {
            this.textareaTarget.focus();
        }, 50);
    }

    // Keep the red overlay in sync while the trader edits the codes.
    codesChanged() {
        if (this.reviewMode) {
            this.renderHighlight();
        }
    }

    syncHighlightScroll() {
        if (!this.hasHighlightTarget) return;
        this.highlightTarget.scrollTop = this.textareaTarget.scrollTop;
        this.highlightTarget.scrollLeft = this.textareaTarget.scrollLeft;
    }

    async submit() {
        const raw = this.textareaTarget.value.trim();

        if (!raw) {
            alert(this.emptyLabelValue || "Моля въведете поне един SKU");
            return;
        }

        const { skuList, qtyList } = this.parseLines(raw);
        const invalid = await this.validateCodes(skuList);

        if (invalid === null) {
            alert(this.errorLabelValue || "Възникна грешка при проверката на кодовете. Опитайте отново.");
            return;
        }

        // Everything is fine: behave exactly as before.
        if (invalid.length === 0) {
            this.fillAndSubmit(skuList, qtyList);
            return;
        }

        // Second click in review mode with the same set of bad codes: import only the valid ones.
        if (this.reviewMode && this.sameCodes(invalid, this.invalidCodes)) {
            this.fillAndSubmit(skuList, qtyList, invalid);
            return;
        }

        // Intermediate step: show what is wrong, don't touch the cart yet.
        this.enterReviewMode(invalid);
    }

    parseLines(raw) {
        const skuList = [];
        const qtyList = [];

        raw.split(/\n+/).forEach(line => {
            const parts = line.split(/[\s,;]+/).filter(Boolean);
            if (!parts.length) return;

            skuList.push(parts[0]);
            qtyList.push(parts[1] || 1);
        });

        return { skuList, qtyList };
    }

    /**
     * @returns {Promise<string[]|null>} invalid codes, or null when the request failed
     */
    async validateCodes(skuList) {
        if (!this.validateUrlValue) {
            return [];
        }

        this.setBusy(true);

        try {
            const params = new URLSearchParams();
            skuList.forEach(sku => params.append('codes[]', sku));

            const response = await fetch(this.validateUrlValue, {
                method: "POST",
                headers: {
                    "Content-Type": "application/x-www-form-urlencoded",
                    "X-Requested-With": "XMLHttpRequest"
                },
                body: params.toString()
            });

            if (!response.ok) return null;

            const data = await response.json();
            return Array.isArray(data.invalid) ? data.invalid : [];
        } catch (e) {
            return null;
        } finally {
            this.setBusy(false);
        }
    }

    fillAndSubmit(skuList, qtyList, invalidCodes = []) {
        const invalidSet = new Set(invalidCodes);
        const validSku = [];
        const validQty = [];

        skuList.forEach((sku, i) => {
            if (!invalidSet.has(sku)) {
                validSku.push(sku);
                validQty.push(qtyList[i]);
            }
        });

        if (!validSku.length) {
            alert(this.noValidLabelValue || "Няма валидни кодове за импорт.");
            return;
        }

        this.codeTarget.value = validSku.join(",");
        this.quantityTarget.value = validQty.join(",");
        this.codeTarget.form.submit();
    }

    enterReviewMode(invalidCodes) {
        this.reviewMode = true;
        this.invalidCodes = invalidCodes;

        this.renderHighlight();

        if (this.hasCodesWrapTarget) this.codesWrapTarget.classList.add('has-invalid');
        if (this.hasInvalidHintTarget) this.invalidHintTarget.classList.remove('d-none');
        this.setSubmitLabel(this.importValidLabelValue);
    }

    exitReviewMode() {
        this.reviewMode = false;
        this.invalidCodes = [];

        if (this.hasHighlightTarget) this.highlightTarget.innerHTML = '';
        if (this.hasCodesWrapTarget) this.codesWrapTarget.classList.remove('has-invalid');
        if (this.hasInvalidHintTarget) this.invalidHintTarget.classList.add('d-none');
        this.setSubmitLabel(this.loadLabelValue);
    }

    renderHighlight() {
        if (!this.hasHighlightTarget) return;

        const invalidSet = new Set(this.invalidCodes);
        const html = this.textareaTarget.value
            .split('\n')
            .map(line => this.highlightLine(line, invalidSet))
            .join('\n');

        // Trailing newline needs a visible line so the overlay height matches the textarea.
        this.highlightTarget.innerHTML = html + '\n';
        this.syncHighlightScroll();
    }

    highlightLine(line, invalidSet) {
        const match = line.match(/^(\s*)([^\s,;]+)(.*)$/);
        if (!match) {
            return this.escapeHtml(line);
        }

        const [, leading, sku, rest] = match;
        const skuHtml = invalidSet.has(sku)
            ? `<span class="invalid-sku">${this.escapeHtml(sku)}</span>`
            : this.escapeHtml(sku);

        return this.escapeHtml(leading) + skuHtml + this.escapeHtml(rest);
    }

    sameCodes(a, b) {
        if (a.length !== b.length) return false;
        const sortedA = [...a].sort();
        const sortedB = [...b].sort();
        return sortedA.every((code, i) => code === sortedB[i]);
    }

    setBusy(busy) {
        if (this.hasSubmitButtonTarget) {
            this.submitButtonTarget.disabled = busy;
        }
    }

    setSubmitLabel(label) {
        if (this.hasSubmitButtonTarget && label) {
            this.submitButtonTarget.textContent = label;
        }
    }

    escapeHtml(value) {
        return String(value)
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;');
    }

    resetForm() {
        this.cityTarget.value     = "";
        this.codeTarget.value     = "";
        this.quantityTarget.value = "";
        this.textareaTarget.value = "";
        this.singleFormTarget.classList.add("d-none");
        this.exitReviewMode();

        this.element
            .querySelectorAll('input[name="city"]')
            .forEach(r => r.checked = false);
    }
}
